if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        // The button label may contain element children that are populated at
        // runtime by a bundle/pricing app (e.g. .cta-dot, .compare-price,
        // .current-price). Assigning textContent to the label would delete
        // them, so remember the original leading text and rewrite only that
        // text node later. See setLabelText().
        this.originalLabelText = this.getLabelTextNode()?.textContent.trim() || null;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // Returns the first meaningful text node inside the button label, i.e.
      // the "Add To Cart" / "Sold out" wording, ignoring element children.
      getLabelTextNode() {
        if (!this.submitButtonText) return null;
        return (
          Array.prototype.find.call(
            this.submitButtonText.childNodes,
            (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
          ) || null
        );
      }

      // Replaces the label wording WITHOUT destroying element children.
      //
      // Stock Dawn does `label.textContent = text`, which removes every child
      // node. On this theme the label also holds price markup injected at
      // runtime by the bundle app (.compare-price / .current-price), so the
      // stock behaviour wiped the price on every variant change and the app
      // had nothing left to repopulate. Only the text node is rewritten here.
      setLabelText(text) {
        if (!this.submitButtonText || !text) return;

        // No element children: safe to use the simple path.
        if (this.submitButtonText.children.length === 0) {
          this.submitButtonText.textContent = text;
          return;
        }

        const textNode = this.getLabelTextNode();
        if (textNode) {
          // Trailing space keeps spacing before the following element child.
          textNode.textContent = text + ' ';
        } else {
          this.submitButtonText.insertBefore(document.createTextNode(text + ' '), this.submitButtonText.firstChild);
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.setLabelText(text);
        } else {
          this.submitButton.removeAttribute('disabled');
          // Prefer the label's own original wording so custom casing such as
          // "Add To Cart" survives; fall back to the theme locale string.
          this.setLabelText(this.originalLabelText || window.variantStrings.addToCart);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
